import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import type { WsPermission, WsRole } from '@zentera/shared';
import { Spinner } from '@zentera/ui';
import { api, ApiError } from '@zentera/web-core';

export interface Me {
  user: { id: string; name: string; email: string; role: WsRole; mustChangePassword: boolean };
  permissions: WsPermission[];
  instanceVersion: string;
}

export const meKey = ['me'] as const;

export function useMe() {
  return useQuery({ queryKey: meKey, queryFn: () => api.get<Me>('/api/auth/me'), retry: false, staleTime: 60_000 });
}

export function useCan() {
  const { data } = useMe();
  return (p: WsPermission) => data?.permissions.includes(p) ?? false;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, error, isPending } = useMe();
  const location = useLocation();
  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center text-fg-3">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (error) {
    const next = location.pathname !== '/' ? `?next=${encodeURIComponent(location.pathname)}` : '';
    return <Navigate to={error instanceof ApiError && error.status === 401 ? `/login${next}` : '/login'} replace />;
  }
  if (data.user.mustChangePassword) return <Navigate to="/password" replace />;
  return <>{children}</>;
}
